import { useContext, useEffect, useRef, useState } from "react";
import { useAppLoading } from "../../components/layout/app-loading-provider";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { getRedemptionErrorMessage } from "../../utils/redemption-errors";
import CardComponent from "../common/card-component";
import { GetEventInputValue, ValidateFormView } from "../form/core";
import FixedBottomButton from "../form/FixedBottomButton";
import { InputNumber } from "../form/input-number";
import { PinInput } from "../form/input-pin-number";
import { InvalidInputMessage } from "../form/invalid-input";
import { PrimaryButton } from "../form/primary-button";

const formView = {
  fields: [
    {
      name: "pinCode",
      label: "Pin code",
      required: true,
      autoComplete: "off",
      validate: (value, dataModel) => {
        if (value.length !== 4) return `Pin code should be a 4-digit number`;
        return null;
      },
    },
    {
      name: "amount",
      label: " ",
      type: "number",
      isRequiredMessage: "Amount to redeem from gift card can't be empty",
      required: true,
      validate: (value, dataModel) => {
        if (value <= 0) return `Redeemed amount should be greater than $0.00`;
        return null;
      },
    },
  ],
};

const getFieldValues = (dataModel) => {
  if (!dataModel) return null;

  const remaining = dataModel.totalBillAmount - (dataModel.totalRedeemed || 0);

  const balance = (dataModel.voucher && dataModel.voucher.Balance) || 0;
  const amount = remaining <= balance ? remaining : balance;

  let companyId = null;
  if (dataModel.companyId) {
    companyId = parseInt(dataModel.companyId);
  }

  return {
    companyId,
    cardNumber: dataModel.cardNumber,
    pinCode: dataModel.pinCode,
    amount: amount,
    totalBillAmount: dataModel.totalBillAmount,
  };
};

const normalizeTwoDecimals = (value) => {
  if (value === null || value === undefined || value === "") return value;
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return value;
  return Number(parsed.toFixed(2));
};

export const RedeemGiftCardVoucher = ({
  dataModel,
  transactionGroupId,
  onRedeemSuccess,
  onError,
  onUnauthorised,
  otherPanels,
  serviceDispatcher,
  onUseAnotherVoucher,
}) => {
  const { showLoading, hideLoading } = useAppLoading();
  const { state, dispatch } = useContext(CognitoContext);
  const [formState, setFormState] = useState({
    isProcessing: false,
    transactionGroupId,
  });

  const [model, setModel] = useState(getFieldValues(dataModel) || {});
  const [errors, setErrors] = useState({});
  const currentBalance = (dataModel.voucher && dataModel.voucher.Balance) || 0;
  const [errorText, setErrorText] = useState("");
  const [currentStep, setCurrentStep] = useState(1); // Step 1 by default
  const [pinError, setPinError] = useState(null); // Error message related to PIN
  const changeCardRef = useRef(null);
  const redeemButtonRef = useRef(null);

  useEffect(() => {
    if (changeCardRef?.current) {
      changeCardRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (currentStep === 2 && redeemButtonRef?.current) {
      redeemButtonRef.current.focus();
    }
    setErrorText("");
  }, [currentStep]);
  const onChangeHandler = (event) => {
    setModel({
      ...model,
      [event.target.name]: GetEventInputValue(event, true),
    });
  };

  useEffect(() => {
    if (model.amount === undefined) return;
    const normalized = normalizeTwoDecimals(model.amount);
    if (normalized !== model.amount) {
      setModel((prev) => ({
        ...prev,
        amount: normalized,
      }));
    }
  }, [model.amount]);

  useEffect(() => {
    if (model.pinCode?.length === 4) {
      setCurrentStep(2); // Move to Step 2 if PIN is 4 digits
    }
    setErrorText("");
  }, [model.pinCode]);

  const redeemCard = async () => {
    setErrorText("");
    let service = null;
    showLoading();
    if (serviceDispatcher && typeof serviceDispatcher === "function")
      service = await serviceDispatcher();
    else service = CoreServiceSelector.GetRedemptionService(state.session);

    setFormState({
      ...formState,
      isProcessing: true,
    });

    service
      .redeemCard({
        CompanyId: model.companyId,
        CardNumber: model.cardNumber,
        PinCode: model.pinCode,
        Amount: model.amount,
        TotalBillAmount: model.totalBillAmount,
        TransactionGroupId: formState.transactionGroupId,
      })
      .then((result) => {
        setFormState({
          ...formState,
          isProcessing: false,
        });

        if (result.Success) {
          onRedeemSuccess &&
            onRedeemSuccess({
              redeemedAmount: result.Amount,
              transactionGroupId: result.TransactionGroupId,
            });
        } else {
          if (result.ErrorCode === "41") {
            setPinError(
              "Incorrect pin. Please try again."
            );
            setModel({
              ...model,
              pinCode: "",
            });
            setCurrentStep(1); // Go back to Step 1
          } else {
            setErrorText(getRedemptionErrorMessage(result));
          }
        }
        hideLoading();
      })
      .catch((err) => {
        onUnauthorised &&
          err &&
          err.response &&
          err?.response?.status === 401 &&
          onUnauthorised();

        setFormState({
          ...formState,
          isProcessing: false,
        });
        setErrorText(getRedemptionErrorMessage(err.response?.data));
        hideLoading();
      });
  };


  const onSubmitRedemption = (event) => {
    event.preventDefault();

    const fields = [...formView.fields];

    console.log("dataModel", dataModel);

    if (model.amount > (dataModel?.totalBillAmount || 0)) {
      setErrorText(`You can only redeem what's available on the card.`);
      return;
    }
    const errs = ValidateFormView({ fields, model });
    if (errs && errs.pinCode) {
      setPinError(errs.pinCode);
    }
    if (errs && errs.amount) {
      setErrorText(errs.amount);
    }

    if (!errs || Object.keys(errs).length === 0) {
      setFormState({
        ...formState,
        isProcessing: true,
      });

      redeemCard();
    }
  };

  return (
    <>
      <div>
        <form className={`mb-3 w-full mx-auto items-center text-white ${currentStep === 1 ? 'mt-[30vh]' : currentStep === 2 ? 'mt-[20vh]' : ''}`}>
          {currentStep === 1 && (
            <>
              <label
                className="text-center w-full font-headings-primary font-medium text-[28px] leading-[33.6px] mb-2"
                htmlFor="pinCode"
              >
                {pinError
                  ? "Re-enter the 4 digit pin"
                  : "Enter the 4 digit pin"}
              </label>

              <div>
                <p className="text-center mb-4 text-[16px] font-body-primary">
                </p>

              </div>

              <PinInput
                length={4}
                value={model.pinCode}
                onChange={(newPin) => setModel({ ...model, pinCode: newPin })}
                error={pinError}
                enableAutoFocus={!!model.pinCode}
              />
              <div>
                <p className="text-center mb-2 text-[16px] font-body-primary mt-4">
                  Card #: {dataModel?.cardNumber || ""}
                </p>
                <p
                  className="text-center text-[16px] font-headings-primary text-chilli hover:cursor-pointer"
                  onClick={onUseAnotherVoucher}
                >
                  Change card
                </p>
              </div>
            </>
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <>
              <div className="w-full flex justify-center mt-10 ">
                <CardComponent
                  cardText={`${dataModel?.cardNumber || ""}`}
                  amountText={`$ ${currentBalance?.toFixed(2)}`}
                />
              </div>

              <label
                className="text-center w-full font-headings-primary font-medium text-[28px] leading-[33.6px] mb-2 mt-[2.5rem]"
                htmlFor="amount"
              >
                Enter amount to redeem
              </label>

              <div>
                <p className="text-center mb-4 text-[16px] font-body-primary"
                  ref={changeCardRef}
                >
                  Redeem the full balance or choose another amount.
                </p>
              </div>

              <InputNumber
                key={formView.fields[1].name}
                dataModel={model}
                {...formView.fields[1]}
                errorsModel={errors}
                min={0.01}
                max={currentBalance}
                prefix="$"
                decimalScale={2}
                fixedDecimalScale={true}
                onChange={onChangeHandler}
                className="mb-6 md:max-w-[500px] mx-auto"
              />

              <InvalidInputMessage errorMessage={errorText} />

              <div>{otherPanels}</div>

              <FixedBottomButton >
                <PrimaryButton
                  ref={redeemButtonRef}
                  type="submit"
                  className=" md:max-w-[500px] mx-auto"
                  onClick={onSubmitRedemption}
                >
                  Redeem{" "}
                  {`$${model.amount ? parseFloat(model.amount).toFixed(2) : "0.00"
                    }`}
                </PrimaryButton>
                {/* <SecondaryButton
                                  className="md:max-w-[500px] mx-auto"
                  onClick={onUseAnotherVoucher}
                >
                  Use another card/certificate
                </SecondaryButton> */}
              </FixedBottomButton>
            </>
          )}
        </form>
      </div>
    </>
  );
};
